import { Link } from 'react-router';
import { useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  LoginSchema,
  RegisterSchema,
  type LoginInput,
  type RegisterInput,
} from '@dzseguidores/types';
import { Button, Input } from '@dzseguidores/ui';
import { ApiError, api } from '@/lib/api-client';
import { setAccessToken } from '@/lib/auth';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SEC = 60;

function rolesFromAccessToken(token: string): string[] {
  try {
    const payload = JSON.parse(atob(token.split('.')[1] ?? '')) as { roles?: string[] };
    return Array.isArray(payload.roles) ? payload.roles : [];
  } catch {
    return [];
  }
}

function redirectAfterAuth(accessToken: string, rolesHint?: string[]) {
  setAccessToken(accessToken);
  const roles = rolesHint?.length ? rolesHint : rolesFromAccessToken(accessToken);
  const dest = roles.includes('ADMIN') || roles.includes('STAFF') ? '/admin' : '/conta/pedidos';
  window.location.assign(dest);
}

function apiErrorMessage(err: unknown, fallback: string) {
  if (err instanceof ApiError && err.message) return err.message;
  return fallback;
}

export function LoginPage() {
  const submitting = useRef(false);
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({ resolver: zodResolver(LoginSchema) });

  return (
    <div className="container-page flex min-h-[70vh] items-center justify-center py-10">
      <div className="glass w-full max-w-md rounded-2xl p-8">
        <h1 className="text-2xl font-semibold text-fg">Entrar</h1>
        <p className="mt-2 text-sm text-muted">Acesse seus pedidos e o painel administrativo.</p>
        <form
          className="mt-6 space-y-4"
          onSubmit={handleSubmit(async (data) => {
            if (submitting.current) return;
            submitting.current = true;
            setFormError(null);
            try {
              const result = await api.login(data);
              if (!result?.accessToken) {
                setFormError('Resposta de login inválida.');
                submitting.current = false;
                return;
              }
              redirectAfterAuth(result.accessToken, result.roles);
            } catch (err) {
              submitting.current = false;
              if (err instanceof ApiError && err.status === 403) {
                setFormError(
                  'E-mail ainda não confirmado. Volte ao cadastro para receber um novo código.',
                );
              } else if (err instanceof ApiError) {
                setFormError('E-mail ou senha inválidos.');
              } else {
                setFormError('Falha ao conectar com a API.');
              }
            }
          })}
        >
          {formError && (
            <p className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm text-danger">
              {formError}
            </p>
          )}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-fg" htmlFor="login-email">
              E-mail
            </label>
            <Input
              id="login-email"
              type="email"
              placeholder="voce@email.com"
              autoComplete="email"
              {...register('email')}
            />
            {errors.email && <p className="mt-1 text-xs text-danger">{errors.email.message}</p>}
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-fg" htmlFor="login-password">
              Senha
            </label>
            <Input
              id="login-password"
              type="password"
              placeholder="Sua senha"
              autoComplete="current-password"
              {...register('password')}
            />
            {errors.password && (
              <p className="mt-1 text-xs text-danger">{errors.password.message}</p>
            )}
          </div>
          <Button type="submit" className="w-full" disabled={isSubmitting || submitting.current}>
            {isSubmitting ? 'Entrando…' : 'Entrar'}
          </Button>
        </form>
        <div className="mt-4 flex justify-between text-sm">
          <Link to="/recuperar-senha" className="text-muted hover:text-fg">
            Esqueci a senha
          </Link>
          <Link to="/cadastro" className="text-muted hover:text-fg">
            Criar conta
          </Link>
        </div>
      </div>
    </div>
  );
}

export function RegisterPage() {
  const submitting = useRef(false);
  const otpInputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const [step, setStep] = useState<'form' | 'otp'>('form');
  const [pending, setPending] = useState<RegisterInput | null>(null);
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [formError, setFormError] = useState<string | null>(null);
  const [devCode, setDevCode] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterInput>({ resolver: zodResolver(RegisterSchema) });

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = window.setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [resendIn]);

  async function startOtp(data: RegisterInput) {
    const result = await api.registerRequestOtp(data);
    setPending(data);
    setStep('otp');
    setOtp(Array(OTP_LENGTH).fill(''));
    setResendIn(RESEND_COOLDOWN_SEC);
    setDevCode(result.devCode ?? null);
    setFormError(null);
    window.setTimeout(() => otpInputsRef.current[0]?.focus(), 50);
  }

  function setOtpDigit(index: number, value: string) {
    const digit = value.replace(/\D/g, '').slice(-1);
    setOtp((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
    if (digit && index < OTP_LENGTH - 1) {
      otpInputsRef.current[index + 1]?.focus();
    }
  }

  function onOtpKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
    }
  }

  function onOtpPaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setOtp(next);
    otpInputsRef.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  }

  return (
    <div className="container-page flex min-h-[70vh] items-center justify-center py-10">
      <div className="glass w-full max-w-md rounded-2xl p-8">
        {step === 'form' ? (
          <>
            <h1 className="text-2xl font-semibold text-fg">Criar conta</h1>
            <p className="mt-2 text-sm text-muted">
              Enviaremos um código de 6 dígitos para confirmar seu e-mail.
            </p>
            <form
              className="mt-6 space-y-4"
              onSubmit={handleSubmit(async (data) => {
                if (submitting.current) return;
                submitting.current = true;
                setFormError(null);
                try {
                  await startOtp(data);
                } catch (err) {
                  setFormError(
                    apiErrorMessage(
                      err,
                      err instanceof ApiError
                        ? 'Não foi possível iniciar o cadastro.'
                        : 'Falha de conexão.',
                    ),
                  );
                } finally {
                  submitting.current = false;
                }
              })}
            >
              {formError && <p className="text-sm text-danger">{formError}</p>}
              <Input placeholder="Nome" autoComplete="name" {...register('name')} />
              {errors.name && <p className="text-xs text-danger">{errors.name.message}</p>}
              <Input
                type="email"
                placeholder="E-mail"
                autoComplete="email"
                {...register('email')}
              />
              {errors.email && <p className="text-xs text-danger">{errors.email.message}</p>}
              <Input
                type="password"
                placeholder="Senha (mín. 8)"
                autoComplete="new-password"
                {...register('password')}
              />
              {errors.password && <p className="text-xs text-danger">{errors.password.message}</p>}
              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting || submitting.current}
              >
                {isSubmitting ? 'Enviando código…' : 'Continuar'}
              </Button>
            </form>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-semibold text-fg">Confirme seu e-mail</h1>
            <p className="mt-2 text-sm text-muted">
              Digite o código enviado para <span className="text-fg">{pending?.email}</span>.
            </p>
            {devCode && (
              <p className="mt-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-xs text-muted">
                Dev: código <span className="font-mono text-fg">{devCode}</span>
              </p>
            )}
            <form
              className="mt-6 space-y-4"
              onSubmit={async (e) => {
                e.preventDefault();
                if (!pending || submitting.current) return;
                const code = otp.join('');
                if (code.length !== OTP_LENGTH) {
                  setFormError('Informe o código de 6 dígitos.');
                  return;
                }
                submitting.current = true;
                setFormError(null);
                try {
                  const result = await api.registerConfirm({ ...pending, code });
                  redirectAfterAuth(result.accessToken, result.roles);
                } catch (err) {
                  submitting.current = false;
                  setFormError(apiErrorMessage(err, 'Código inválido ou expirado.'));
                  setOtp(Array(OTP_LENGTH).fill(''));
                  otpInputsRef.current[0]?.focus();
                }
              }}
            >
              {formError && <p className="text-sm text-danger">{formError}</p>}
              <div className="flex justify-between gap-2" onPaste={onOtpPaste}>
                {otp.map((digit, index) => (
                  <Input
                    key={index}
                    ref={(el) => {
                      otpInputsRef.current[index] = el;
                    }}
                    inputMode="numeric"
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    maxLength={1}
                    className="h-12 w-11 text-center font-mono text-lg"
                    value={digit}
                    onChange={(ev) => setOtpDigit(index, ev.target.value)}
                    onKeyDown={(ev) => onOtpKeyDown(index, ev)}
                    aria-label={`Dígito ${index + 1}`}
                  />
                ))}
              </div>
              <Button type="submit" className="w-full" disabled={submitting.current}>
                Confirmar e criar conta
              </Button>
            </form>
            <div className="mt-4 flex flex-col gap-2 text-center text-sm">
              <button
                type="button"
                className="text-muted hover:text-fg disabled:opacity-50"
                disabled={resendIn > 0 || submitting.current || !pending}
                onClick={async () => {
                  if (!pending || submitting.current) return;
                  submitting.current = true;
                  setFormError(null);
                  try {
                    const result = await api.registerResendOtp({ email: pending.email });
                    setResendIn(RESEND_COOLDOWN_SEC);
                    setDevCode(result.devCode ?? null);
                    setOtp(Array(OTP_LENGTH).fill(''));
                    otpInputsRef.current[0]?.focus();
                  } catch (err) {
                    setFormError(apiErrorMessage(err, 'Não foi possível reenviar o código.'));
                  } finally {
                    submitting.current = false;
                  }
                }}
              >
                {resendIn > 0 ? `Reenviar código em ${resendIn}s` : 'Reenviar código'}
              </button>
              <button
                type="button"
                className="text-muted hover:text-fg"
                onClick={() => {
                  setStep('form');
                  setPending(null);
                  setFormError(null);
                  setDevCode(null);
                  setOtp(Array(OTP_LENGTH).fill(''));
                }}
              >
                Alterar e-mail
              </button>
            </div>
          </>
        )}
        <p className="mt-4 text-center text-sm text-muted">
          Já tem conta?{' '}
          <Link to="/login" className="underline hover:text-fg">
            Entrar
          </Link>
        </p>
      </div>
    </div>
  );
}

export function ForgotPasswordPage() {
  return (
    <div className="container-page flex min-h-[70vh] items-center justify-center py-10">
      <div className="glass w-full max-w-md rounded-2xl p-8">
        <h1 className="text-2xl font-semibold text-fg">Recuperar senha</h1>
        <p className="mt-2 text-sm text-muted">
          Enviaremos um link de redefinição para seu e-mail.
        </p>
        <form className="mt-6 space-y-4" onSubmit={(e) => e.preventDefault()}>
          <Input type="email" placeholder="E-mail" name="email" />
          <Button type="submit" className="w-full">
            Enviar link
          </Button>
        </form>
        <Link to="/login" className="mt-4 block text-center text-sm text-muted hover:text-fg">
          Voltar ao login
        </Link>
      </div>
    </div>
  );
}
